import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
// @ts-ignore
import { Html5Qrcode } from "html5-qrcode";
import { logout } from "../apis/backend";
import {
  getStudentActiveSessions,
  submitQrAttendance,
  getStudentHistory,
  getStudentSummary,
} from "../apis/attendanceApi";
import Sidebar from "./Utils/Sidebar";
import { QrCode, CheckCircle2, Clock, MapPin, Award, AlertCircle, RefreshCw, Key, LogOut } from "lucide-react";
import "./Upload.css";

export default function Attendance() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeSessions, setActiveSessions] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [summaries, setSummaries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // OTP inputs map per session
  const [otpInputs, setOtpInputs] = useState<Record<string, string>>({});
  const [submittingOtp, setSubmittingOtp] = useState<string | null>(null);

  // Scanning state
  const [scanningSessionId, setScanningSessionId] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<string>("");
  const html5QrcodeRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = "reader-container";

  const loadData = async () => {
    setLoading(true);
    try {
      const [actRes, histRes, sumRes] = await Promise.all([
        getStudentActiveSessions(),
        getStudentHistory(0, 20),
        getStudentSummary(),
      ]);
      setActiveSessions(actRes || []);
      setHistory(histRes?.records || []);
      setSummaries(sumRes || []);
    } catch (err: any) {
      console.error(err);
      setMessage({ text: err.message || "Failed to load attendance data", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const startScanner = async (sessionId: string) => {
    setScanningSessionId(sessionId);
    setScanStatus("Initializing camera...");
    setMessage(null);

    setTimeout(async () => {
      try {
        if (html5QrcodeRef.current) {
          try {
            await html5QrcodeRef.current.stop();
          } catch (e) {}
        }
        const html5Qr = new Html5Qrcode(scannerContainerId);
        html5QrcodeRef.current = html5Qr;

        await html5Qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText: string) => {
            onQrScanned(sessionId, decodedText);
          },
          () => {}
        );
        setScanStatus("Camera active. Point at coordinator's QR code...");
      } catch (err: any) {
        console.error("Camera error:", err);
        setScanStatus("Camera access denied or unreadable. Use the 6-Digit OTP option below.");
        setMessage({ text: "Camera error: " + (err.message || err), type: "error" });
      }
    }, 300);
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current.clear();
      } catch (e) {}
      html5QrcodeRef.current = null;
    }
    setScanningSessionId(null);
    setScanStatus("");
  };

  const onQrScanned = async (sessionId: string, decodedText: string) => {
    await stopScanner();
    setScanStatus("QR scanned! Capturing GPS location...");

    let token = "";
    try {
      const parsed = JSON.parse(decodedText);
      token = parsed.token || decodedText;
    } catch {
      token = decodedText;
    }

    submitAttendanceWithToken(sessionId, token);
  };

  const handleOtpSubmit = (sessionId: string, e: React.FormEvent) => {
    e.preventDefault();
    const otp = (otpInputs[sessionId] || "").trim();
    if (!otp || otp.length !== 6) {
      setMessage({ text: "Please enter a valid 6-digit OTP code", type: "error" });
      return;
    }
    submitAttendanceWithToken(sessionId, otp);
  };

  const submitAttendanceWithToken = (sessionId: string, token: string) => {
    if (!navigator.geolocation) {
      setMessage({ text: "Geolocation is not supported by your browser.", type: "error" });
      return;
    }

    setSubmittingOtp(sessionId);
    setMessage(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          const accuracy = position.coords.accuracy;

          const res = await submitQrAttendance({
            session_id: sessionId,
            qr_token: token,
            student_lat: lat,
            student_lon: lon,
            location_accuracy: accuracy,
          });

          setMessage({ text: res.message || "Attendance marked successfully!", type: "success" });
          setOtpInputs((prev) => ({ ...prev, [sessionId]: "" }));
          loadData();
        } catch (err: any) {
          setMessage({ text: err.message || "Failed to submit attendance", type: "error" });
        } finally {
          setSubmittingOtp(null);
        }
      },
      (geoErr) => {
        console.error(geoErr);
        setSubmittingOtp(null);
        setMessage({ text: "GPS Location Access Denied: " + geoErr.message, type: "error" });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleLogout = async () => {
    const ok = await logout();
    if (ok) {
      navigate("/");
    }
  };

  return (
    <div className="page12Container">
      <Sidebar
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
        activeSection="dashboard"
      />

      <main className={`mainContent ${sidebarCollapsed ? "sidebarCollapsed" : ""}`}>
        <header className="contentHeader d-flex justify-content-between align-items-center">
          <h1 className="pageTitle">Student Attendance</h1>

          <div className="d-flex align-items-center gap-3">
            <div className="userProfile" style={{ position: "relative" }}>
              <div
                className="profileCircle"
                onClick={() => setShowDropdown((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <span className="profileInitial">S</span>
              </div>

              {showDropdown && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "45px",
                    background: "white",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                    padding: "10px",
                    minWidth: "130px",
                    zIndex: 100,
                  }}
                >
                  <button
                    onClick={handleLogout}
                    style={{
                      width: "100%",
                      background: "none",
                      border: "none",
                      textAlign: "left",
                      padding: "8px",
                      cursor: "pointer",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="contentBody p-4">
          {message && (
            <div className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show`} role="alert">
              {message.type === "success" ? <CheckCircle2 className="me-2" size={18} /> : <AlertCircle className="me-2" size={18} />}
              {message.text}
              <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
            </div>
          )}

          {/* ACTIVE SESSIONS PANEL */}
          <section className="mb-5">
            <h2 className="fs-5 fw-bold mb-3 d-flex align-items-center gap-2 text-primary">
              <QrCode size={22} /> Active Class Sessions
            </h2>

            {loading ? (
              <div className="text-muted">Loading active sessions...</div>
            ) : activeSessions.length === 0 ? (
              <div className="card card-body text-center py-4 bg-light text-muted">
                No active attendance sessions currently open for your enrolled courses.
              </div>
            ) : (
              <div className="row g-3">
                {activeSessions.map((session) => (
                  <div key={session.session_id} className="col-md-6 col-lg-6">
                    <div className="card shadow-sm border-0 h-100">
                      <div className="card-body d-flex flex-column justify-content-between">
                        <div>
                          <span className="badge bg-success mb-2">LIVE SESSION</span>
                          <h5 className="card-title fw-bold text-dark">{session.program_name}</h5>
                          <p className="card-subtitle text-muted mb-2">{session.session_label}</p>
                          <small className="text-secondary d-block">
                            Started: {new Date(session.actual_started_at).toLocaleTimeString()}
                          </small>
                        </div>

                        <div className="mt-3 pt-3 border-top">
                          {session.already_marked ? (
                            <div className="alert alert-success m-0 py-2 d-flex align-items-center gap-2">
                              <CheckCircle2 size={18} /> Attendance Marked
                            </div>
                          ) : (
                            <div className="d-flex flex-column gap-3">
                              {/* Option A: Camera QR Scanner */}
                              <button
                                className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                onClick={() => startScanner(session.session_id)}
                                disabled={scanningSessionId === session.session_id}
                              >
                                <QrCode size={18} /> Scan Attendance QR
                              </button>

                              {/* Option B: Enter 6-Digit OTP */}
                              <form onSubmit={(e) => handleOtpSubmit(session.session_id, e)}>
                                <div className="input-group">
                                  <span className="input-group-text bg-light">
                                    <Key size={16} className="text-primary" />
                                  </span>
                                  <input
                                    type="text"
                                    maxLength={6}
                                    className="form-control text-center fw-bold tracking-wider"
                                    placeholder="Enter 6-Digit OTP"
                                    value={otpInputs[session.session_id] || ""}
                                    onChange={(e) =>
                                      setOtpInputs({
                                        ...otpInputs,
                                        [session.session_id]: e.target.value.replace(/\D/g, ""),
                                      })
                                    }
                                  />
                                  <button
                                    type="submit"
                                    className="btn btn-outline-primary fw-semibold"
                                    disabled={submittingOtp === session.session_id || (otpInputs[session.session_id] || "").length !== 6}
                                  >
                                    {submittingOtp === session.session_id ? "Submitting..." : "Submit OTP"}
                                  </button>
                                </div>
                              </form>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* CAMERA SCANNER MODAL / CONTAINER */}
          {scanningSessionId && (
            <div className="card shadow mb-5 border-primary">
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <span className="fw-bold d-flex align-items-center gap-2">
                  <QrCode size={20} /> Point Camera at QR Display
                </span>
                <button className="btn btn-sm btn-light" onClick={stopScanner}>
                  Cancel / Close
                </button>
              </div>
              <div className="card-body text-center">
                <p className="text-muted mb-3">{scanStatus}</p>
                <div id={scannerContainerId} style={{ width: "100%", maxWidth: "400px", margin: "0 auto", minHeight: "250px" }}></div>
              </div>
            </div>
          )}

          {/* COURSE ATTENDANCE SUMMARY */}
          <section className="mb-5">
            <h2 className="fs-5 fw-bold mb-3 d-flex align-items-center gap-2 text-dark">
              <Award size={22} color="#0d6efd" /> Course-Wise Attendance Percentage
            </h2>
            <div className="table-responsive bg-white rounded shadow-sm p-3">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Course Name</th>
                    <th className="text-center">Conducted</th>
                    <th className="text-center">Present</th>
                    <th className="text-center">Absent</th>
                    <th className="text-center">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        No completed course records found.
                      </td>
                    </tr>
                  ) : (
                    summaries.map((sum) => (
                      <tr key={sum.program_id}>
                        <td className="fw-semibold">{sum.program_name}</td>
                        <td className="text-center">{sum.conducted_sessions}</td>
                        <td className="text-center text-success fw-bold">{sum.present_sessions}</td>
                        <td className="text-center text-danger">{sum.absent_sessions}</td>
                        <td className="text-center">
                          <span className={`badge ${sum.percentage >= 75 ? "bg-success" : sum.percentage >= 50 ? "bg-warning text-dark" : "bg-danger"}`}>
                            {sum.percentage}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ATTENDANCE HISTORY */}
          <section>
            <h2 className="fs-5 fw-bold mb-3 d-flex align-items-center gap-2 text-dark">
              <Clock size={22} color="#6c757d" /> Recent Attendance History
            </h2>
            <div className="table-responsive bg-white rounded shadow-sm p-3">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Course</th>
                    <th>Session</th>
                    <th>Date & Time</th>
                    <th>Method</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        No historical attendance records.
                      </td>
                    </tr>
                  ) : (
                    history.map((h) => (
                      <tr key={h.id}>
                        <td className="fw-semibold">{h.program_name || "—"}</td>
                        <td>{h.session_label || "—"}</td>
                        <td>{new Date(h.marked_at).toLocaleString()}</td>
                        <td>
                          <span className="badge bg-secondary">{h.method}</span>
                        </td>
                        <td>
                          <span className="badge bg-success">{h.status || "PRESENT"}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
